import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vehicleCategoryApi } from "../../../modules/vehicle/category/api";
import {
  CertificateVehicleCategoryFilterPageRequest,
  CreateUpdateCertificateVehicleCategory,
} from "../../../modules/vehicle/category/types";
import { EntityObject } from "../../types";

export const VEHICLE_CATEGORY_QUERY_KEYS = {
  filterVehicleCategories: (payload?: CertificateVehicleCategoryFilterPageRequest) =>
    payload ? ["filterVehicleCategories", payload] : ["filterVehicleCategories"],
  createVehicleCategory: () => ["createVehicleCategory"],
  updateVehicleCategory: () => ["updateVehicleCategory"],
  deleteVehicleCategory: () => ["deleteVehicleCategory"],
};

export const useFilterVehicleCategories = (payload: CertificateVehicleCategoryFilterPageRequest) => {
  return useQuery({
    queryKey: VEHICLE_CATEGORY_QUERY_KEYS.filterVehicleCategories(payload),
    queryFn: async () => {
      const { data } = await vehicleCategoryApi.filterVehicleCategories(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateVehicleCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_CATEGORY_QUERY_KEYS.createVehicleCategory(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCertificateVehicleCategory) => {
      const { data } = await vehicleCategoryApi.createVehicleCategory(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_CATEGORY_QUERY_KEYS.filterVehicleCategories() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateVehicleCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_CATEGORY_QUERY_KEYS.updateVehicleCategory(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCertificateVehicleCategory>) => {
      const { data } = await vehicleCategoryApi.updateVehicleCategory(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_CATEGORY_QUERY_KEYS.filterVehicleCategories() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteVehicleCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_CATEGORY_QUERY_KEYS.deleteVehicleCategory(),
    mutationFn: (id: string) => {
      return vehicleCategoryApi.deleteVehicleCategory({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_CATEGORY_QUERY_KEYS.filterVehicleCategories() });
      Notification.itemDeletedNotification();
    },
  });
};
